import moment from "moment";

import { valueof } from "api/common/utils/type.utils";
import { PclProduct } from "features/public/Products/PCL/context/PclContextProvider";
import { PclFilterOptions } from "features/public/Products/PCL/PublicProducts.screen";
import { filterExact, filterIncludes } from "utils/filter";
import { alphaSort, optionAlphaSort } from "utils/sort";

export const generateProtectionProfileOptions = (
  products: PclProduct[],
): string[] => {
  return [
    ...new Set(
      products.flatMap((product) =>
        product.protection_profiles.map((pp) => pp.pp_name.trim()),
      ),
    ),
  ]
    .filter((x) => x)
    .map((item) => item?.toString() ?? "")
    .sort(alphaSort);
};

export const generateTechTypeOptions = (products: PclProduct[]): string[] => {
  if (!products?.length) return [];

  return [
    ...new Set(
      products.flatMap((product) =>
        product.tech_types?.map((tech_type) => tech_type.trim()),
      ),
    ),
  ]
    .filter((x) => x)
    .map((item) => item?.toString() ?? "")
    .sort(alphaSort);
};

export const buildUniqueStrings = (
  data: PclProduct[],
  prop: keyof PclProduct,
) => {
  if (!data.length) return [];

  const allOptions = data.map((item) => item[prop]);
  const uniqueOptions = new Set(allOptions);
  return [...uniqueOptions]
    .filter((x) => x)
    .map((item) => item?.toString() ?? "")
    .sort(alphaSort);
};

export const buildUniqueOptions = (
  data: PclProduct[],
  prop: keyof PclProduct,
) => {
  if (!data.length) return [];

  const allOptions = data.map((item) => item[prop]);
  const uniqueOptions = new Set(allOptions);
  return [...uniqueOptions]
    .filter((x) => x)
    .map((item) => ({
      value: item?.toString() ?? "",
      label: item?.toString() ?? "",
    }))
    .sort(optionAlphaSort);
};

// CANNED FILTERS
export const filterByTechType = (
  products: PclProduct[],
  techType: string,
): PclProduct[] => {
  return filterIncludes(products, "tech_types", techType);
};

export const filterByVendor = (
  products: PclProduct[],
  vendor: string,
): PclProduct[] => {
  return filterExact(products, "vendor_id_name", vendor);
};

export const filterByCountry = (
  products: PclProduct[],
  country: string,
): PclProduct[] => {
  return filterExact(products, "submitting_country_id_name", country);
};

export const filterByPp = (
  products: PclProduct[],
  protectionProfile: string,
): PclProduct[] => {
  if (!protectionProfile) return products;
  return products.filter((product) =>
    product.protection_profiles.some((pp) => pp.pp_name === protectionProfile),
  );
};

export const filterByStatus = (
  products: PclProduct[],
  status: string,
): PclProduct[] => {
  return filterExact(products, "status_sort", status);
};

export const filterBySearch = (
  products: PclProduct[],
  search: string,
): PclProduct[] => {
  if (!search) return products;

  const normalizeString = (str: valueof<PclProduct>): string => {
    if (!str) return "";

    if (typeof str === "string") return str.toLowerCase();

    if (typeof str === "number") return str.toString();
    return str.toString().toLowerCase().trim();
  };

  const normalizedSearch = normalizeString(search);
  const dateSearch = normalizedSearch.replace(/[/-]/g, "");

  const filtered = products.filter((product) => {
    const vid = normalizeString(product.v_id);
    const vendor = normalizeString(product.vendor_id_name);
    const productName = normalizeString(product.product_name);
    const lab = normalizeString(product.assigned_lab_name);
    const pps = product.protection_profiles
      .map(
        (pp) =>
          `${pp.pp_name.toLowerCase() || ""}, ${pp.pp_short_name?.toLowerCase() || ""}`,
      )
      .join(", ");

    // I realize this is janky. please forgive my sins until i got actual date search in TT
    // TODO: Actual date filters
    const { certification_date: cd, am_sort: am } = product;
    const certDate = cd
      ? `${moment(cd).format("MDYYYY")} ${moment(cd).format("MMDDYYYY")}`
      : "";
    const amDate = am
      ? `${moment(am).format("MDYYYY")} ${moment(am).format("MMDDYYYY")}`
      : "";

    const normalizedStrings = [
      vid,
      vendor,
      productName,
      lab,
      pps,
      certDate,
      amDate,
    ];
    const dateStrings = [certDate, amDate];

    return (
      normalizedStrings.some((str) => str.includes(normalizedSearch)) ||
      dateStrings.some((str) => str.includes(dateSearch))
    );
  });

  return filtered;
};

export const filterProducts = (
  products: PclProduct[],
  filters: PclFilterOptions,
) => {
  let filteredProducts = products;
  // status is one of the largest scale classifiers so we filter it first
  filteredProducts = filterByStatus(filteredProducts, filters.status);
  // vendor and country both use exact filters so we do them next for speed
  filteredProducts = filterByVendor(filteredProducts, filters.vendor);
  filteredProducts = filterByCountry(filteredProducts, filters.country);
  // tech and pp both use include filters so we do them next as they will be slightly slower
  filteredProducts = filterByTechType(filteredProducts, filters.techType);
  filteredProducts = filterByPp(filteredProducts, filters.protectionProfile);
  // search is the most expensive filter so we execute it last on the smallest set of data
  filteredProducts = filterBySearch(filteredProducts, filters.search);
  return filteredProducts;
};
